import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import TeslaRobot from '../components/TeslaRobot';
import { digitalEmployees } from '../data';
import { DigitalEmployee } from '../types';

const DemoPage: React.FC = () => {
  // Demo state management
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [selectedEmployee, setSelectedEmployee] = useState<DigitalEmployee | null>(null);
  const [chatMessages, setChatMessages] = useState<Array<{sender: 'user' | 'ai', text: string}>>([]);
  const [userInput, setUserInput] = useState<string>('');
  const [taskInput, setTaskInput] = useState<string>('');
  const [taskAssigned, setTaskAssigned] = useState<boolean>(false);
  const [taskCompleted, setTaskCompleted] = useState<boolean>(false);
  const [taskResult, setTaskResult] = useState<string>('');
  const [isTyping, setIsTyping] = useState<boolean>(false);

  // Demo employee options (using first 3 from our data)
  const demoEmployees = digitalEmployees.slice(0, 3);

  // Handle employee selection
  const selectEmployee = (employee: DigitalEmployee) => {
    setSelectedEmployee(employee);
    setCurrentStep(2);

    // Add initial greeting message
    setTimeout(() => {
      setChatMessages([
        {
          sender: 'ai' as const,
          text: `Hello! I'm ${employee.name}, your AI assistant with expertise in ${employee.skills.join(', ')}. How can I help you today?`
        }
      ]);
    }, 500);
  };

  // Handle sending a chat message
  const sendMessage = (e: React.FormEvent) => {
    e.preventDefault();

    if (!userInput.trim() || !selectedEmployee) return;

    // Add user message
    const newMessages = [...chatMessages, { sender: 'user' as const, text: userInput }];
    setChatMessages(newMessages);
    setUserInput('');

    // Simulate AI typing
    setIsTyping(true);

    // Generate AI response based on user input
    setTimeout(() => {
      setIsTyping(false);

      let response = '';
      const lowerInput = userInput.toLowerCase();

      // Simple response logic based on user input
      if (lowerInput.includes('hello') || lowerInput.includes('hi')) {
        response = `Hello! It's great to connect with you. How can I assist you with ${selectedEmployee.skills.join(', ')} today?`;
      } else if (lowerInput.includes('help') || lowerInput.includes('can you')) {
        response = `I'd be happy to help! I'm specialized in ${selectedEmployee.skills.join(', ')}. Could you provide more details about what you need?`;
      } else if (lowerInput.includes('cost') || lowerInput.includes('price') || lowerInput.includes('rate')) {
        response = `My hourly rate is $${selectedEmployee.hourlyRate}. You only pay for the time you use, and I'm available ${selectedEmployee.availability}.`;
      } else if (lowerInput.includes('skill') || lowerInput.includes('do you know')) {
        response = `My core skills include ${selectedEmployee.skills.join(', ')}. I can handle a wide range of tasks in these areas.`;
      } else if (lowerInput.includes('task') || lowerInput.includes('project') || lowerInput.includes('work')) {
        response = `I'd be happy to work on your task. You can assign me specific work using the "Assign Task" section below. Please provide clear instructions related to my skills: ${selectedEmployee.skills.join(', ')}.`;
        setCurrentStep(3);
      } else {
        response = `Thank you for your message. I'm specialized in ${selectedEmployee.skills.slice(0, 2).join(' and ')}. Would you like to assign me a specific task to demonstrate my capabilities?`;
      }

      setChatMessages([...newMessages, { sender: 'ai' as const, text: response }]);
    }, 1500);
  };

  // Handle assigning a task
  const assignTask = (e: React.FormEvent) => {
    e.preventDefault();

    if (!taskInput.trim() || !selectedEmployee) return;

    setTaskAssigned(true);
    setChatMessages([
      ...chatMessages,
      { sender: 'user' as const, text: `Task assigned: ${taskInput}` },
    ]);

    // Simulate AI working on the task
    setIsTyping(true);

    setTimeout(() => {
      setIsTyping(false);

      // Acknowledge task receipt
      setChatMessages(prev => [
        ...prev,
        {
          sender: 'ai' as const,
          text: `I've received your task: "${taskInput}". I'll start working on it right away.`
        }
      ]);

      // Simulate task progress
      setTimeout(() => {
        setIsTyping(true);

        setTimeout(() => {
          setIsTyping(false);

          // Generate task result based on employee skills and task input
          let result = '';
          const skills = selectedEmployee.skills.map(s => s.toLowerCase());
          const taskLower = taskInput.toLowerCase();

          if (skills.includes('javascript') && (taskLower.includes('code') || taskLower.includes('javascript') || taskLower.includes('program'))) {
            result = `
// Task: ${taskInput}
// Solution:

function processData(data) {
  // Initialize result array
  const results = [];

  // Process each item in the data
  data.forEach(item => {
    // Apply transformation based on business rules
    const transformed = {
      id: item.id,
      name: item.name.toUpperCase(),
      value: item.value * 1.5,
      category: item.category || 'Uncategorized',
      timestamp: new Date().toISOString()
    };

    // Add to results if it meets criteria
    if (transformed.value > 100) {
      results.push(transformed);
    }
  });

  // Return sorted results
  return results.sort((a, b) => b.value - a.value);
}

// Example usage
const sampleData = [
  { id: 1, name: 'item one', value: 75, category: 'A' },
  { id: 2, name: 'item two', value: 100, category: 'B' },
  { id: 3, name: 'item three', value: 125, category: 'A' }
];

const processedData = processData(sampleData);
// console.log(processedData);
// Output will show the processed items with values > 100
`;
          } else if ((skills.includes('data analysis') || skills.includes('analytics')) &&
                    (taskLower.includes('data') || taskLower.includes('analysis') || taskLower.includes('report'))) {
            result = `
# Data Analysis Report

## Executive Summary
Based on the analysis of the provided dataset, we've identified several key trends and insights that can help optimize business performance.

## Key Findings
1. **Customer Segmentation**
   - High-value customers (top 20%) account for 68% of total revenue
   - Geographic distribution shows strongest performance in western regions
   - Customer retention rate is 78% overall, with 92% for high-value segment

2. **Performance Metrics**
   - Year-over-year growth: 23%
   - Quarterly fluctuations show seasonality with Q4 being strongest
   - Product category A shows highest profit margin (42%)

3. **Recommendations**
   - Implement targeted retention program for high-value customers
   - Increase marketing investment in western regions
   - Develop expansion strategy for Product category A
   - Consider seasonal promotions to balance quarterly performance

## Methodology
This analysis was conducted using statistical modeling and segmentation techniques applied to the customer transaction data from the past 24 months.

## Next Steps
I recommend a follow-up analysis on customer acquisition costs by channel to further optimize marketing spend.
`;
          } else if ((skills.includes('marketing') || skills.includes('content creation')) &&
                    (taskLower.includes('marketing') || taskLower.includes('content') || taskLower.includes('social media'))) {
            result = `
# Social Media Content Calendar - Next 2 Weeks

## Week 1

### Monday
**LinkedIn:** Industry insight article on emerging trends
**Twitter:** Quote graphic with industry statistic
**Instagram:** Behind-the-scenes team photo

### Wednesday
**LinkedIn:** Customer success story
**Twitter:** Product tip thread (5 tweets)
**Instagram:** Product feature spotlight

### Friday
**LinkedIn:** Weekly roundup of industry news
**Twitter:** Fun fact Friday + relevant hashtags
**Instagram:** User-generated content highlight

## Week 2

### Monday
**LinkedIn:** Thought leadership article from CEO
**Twitter:** Monday motivation quote
**Instagram:** Product tutorial video

### Wednesday
**LinkedIn:** Infographic on industry statistics
**Twitter:** Poll about industry challenges
**Instagram:** Team member spotlight

### Friday
**LinkedIn:** Upcoming webinar announcement
**Twitter:** FAQ thread addressing common questions
**Instagram:** Weekend-themed product usage ideas

## Content Guidelines
- Maintain brand voice: professional yet approachable
- Include relevant hashtags for each platform
- Aim for 2-3 hashtags on LinkedIn, 5-7 on Twitter, 10-15 on Instagram
- Include call-to-action in each post
- Respond to all comments within 4 hours during business hours
`;
          } else if (skills.includes('project management') &&
                    (taskLower.includes('project') || taskLower.includes('plan') || taskLower.includes('schedule'))) {
            result = `
# Project Implementation Plan

## Project Overview
- **Project Name:** System Integration Initiative
- **Timeline:** 12 weeks
- **Objective:** Seamlessly integrate the new CRM system with existing infrastructure

## Phase 1: Planning & Requirements (Weeks 1-2)
- [ ] Stakeholder interviews
- [ ] System requirements documentation
- [ ] Risk assessment
- [ ] Resource allocation

## Phase 2: Design & Development (Weeks 3-6)
- [ ] Architecture design
- [ ] Integration points mapping
- [ ] Development sprints (2-week cycles)
- [ ] Weekly progress reviews

## Phase 3: Testing (Weeks 7-9)
- [ ] Unit testing
- [ ] Integration testing
- [ ] User acceptance testing
- [ ] Performance testing

## Phase 4: Deployment & Training (Weeks 10-12)
- [ ] Phased deployment plan
- [ ] User training sessions
- [ ] Documentation finalization
- [ ] Support process establishment

## Key Milestones
1. Requirements sign-off: End of Week 2
2. Design approval: End of Week 4
3. Development complete: End of Week 6
4. Testing complete: End of Week 9
5. Go-live: End of Week 11
6. Project closure: End of Week 12

## Resource Requirements
- 2 Project Managers
- 3 Developers
- 1 QA Specialist
- 1 Training Coordinator
- Stakeholder availability for reviews

## Communication Plan
- Daily stand-ups: 15 minutes, 9:00 AM
- Weekly status reports: Fridays by 3:00 PM
- Bi-weekly steering committee meetings
`;
          } else {
            // Generic response for other skills/tasks
            result = `
# Task Completion Report

## Task Summary
I've completed the task: "${taskInput}"

## Approach
1. Analyzed the requirements thoroughly
2. Researched best practices and relevant information
3. Applied my expertise in ${selectedEmployee.skills.join(', ')}
4. Developed a comprehensive solution
5. Reviewed for quality and completeness

## Deliverables
- Complete documentation of the process
- Implementation recommendations
- Analysis of potential challenges
- Timeline for implementation
- Resource requirements

## Next Steps
I recommend reviewing this report and providing feedback on any areas that need adjustment or additional detail. I'm available to discuss any questions you might have about the completed task.

## Additional Resources
Based on this task, you might find these resources helpful:
1. Industry best practices guide
2. Implementation checklist
3. Performance metrics template

Please let me know if you need any clarification or have follow-up tasks related to this work.
`;
          }

          setTaskResult(result);
          setTaskCompleted(true);

          setChatMessages(prev => [
            ...prev,
            {
              sender: 'ai' as const,
              text: `I've completed the task you assigned. Here's the result:`
            }
          ]);

          // Move to final step
          setCurrentStep(4);
        }, 3000);
      }, 2000);
    }, 1500);
  };

  // Handle starting a new demo
  const startNewDemo = () => {
    setCurrentStep(1);
    setSelectedEmployee(null);
    setChatMessages([]);
    setUserInput('');
    setTaskInput('');
    setTaskAssigned(false);
    setTaskCompleted(false);
    setTaskResult('');
  };

  // Handle proceeding to purchase
  const proceedToPurchase = () => {
    if (!selectedEmployee) return;

    // In a real app, this would navigate to checkout with the employee added to cart
    // For demo purposes, we'll just show a success message
    setChatMessages(prev => [
      ...prev,
      {
        sender: 'ai' as const,
        text: `Thank you for your interest! In a real implementation, you would now be directed to complete your purchase of ${selectedEmployee.name}'s services with our subscription plan.`
      }
    ]);
  };

  return (
    <div className="demo-page">
      <div className="demo-header">
        <div className="demo-title-container">
          <h1 className="demo-title">
            Interactive <span className="text-gradient">Demo</span>
          </h1>
          <p className="demo-subtitle">
            Experience how AI employees can transform your workflow
          </p>
        </div>
        <div className="demo-robot">
          <TeslaRobot width={120} height={120} />
        </div>
      </div>

      <div className="demo-progress">
        <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
          <div className="step-number">1</div>
          <div className="step-label">Select AI Employee</div>
        </div>
        <div className="progress-connector"></div>
        <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
          <div className="step-number">2</div>
          <div className="step-label">Chat Interaction</div>
        </div>
        <div className="progress-connector"></div>
        <div className={`progress-step ${currentStep >= 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}>
          <div className="step-number">3</div>
          <div className="step-label">Assign Task</div>
        </div>
        <div className="progress-connector"></div>
        <div className={`progress-step ${currentStep >= 4 ? 'active' : ''}`}>
          <div className="step-number">4</div>
          <div className="step-label">Review Results</div>
        </div>
      </div>

      <div className="demo-content">
        {/* Step 1: Select AI Employee */}
        {currentStep === 1 && (
          <div className="demo-section">
            <h2>Select an AI Employee</h2>
            <p className="section-description">
              Choose an AI employee based on the skills you need for your tasks.
            </p>

            <div className="demo-employees">
              {demoEmployees.map(employee => (
                <div
                  key={employee.id}
                  className="demo-employee-card"
                  onClick={() => selectEmployee(employee)}
                >
                  <div className="demo-employee-avatar">
                    <TeslaRobot width={80} height={80} />
                  </div>
                  <h3>{employee.name}</h3>
                  {/* Hourly rate removed as requested */}
                  <div className="demo-employee-skills">
                    {employee.skills.slice(0, 3).map((skill, index) => (
                      <span key={index} className="demo-skill-tag">{skill}</span>
                    ))}
                  </div>
                  <p className="demo-employee-description">{employee.description}</p>
                  <button className="demo-select-button">Select</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Steps 2-4: Interaction with selected employee */}
        {currentStep > 1 && selectedEmployee && (
          <div className="demo-interaction">
            <div className="demo-employee-profile">
              <div className="profile-header">
                <div className="profile-avatar">
                  <TeslaRobot width={60} height={60} />
                </div>
                <div className="profile-info">
                  <h3>{selectedEmployee.name}</h3>
                  <div className="profile-skills">
                    {selectedEmployee.skills.slice(0, 3).map((skill, index) => (
                      <span key={index} className="profile-skill-tag">{skill}</span>
                    ))}
                  </div>
                </div>
                {/* Hourly rate removed as requested */}
              </div>

              <div className="demo-chat">
                <div className="chat-messages">
                  {chatMessages.map((msg, index) => (
                    <div key={index} className={`chat-message ${msg.sender}`}>
                      {msg.sender === 'ai' && (
                        <div className="message-avatar">
                          <TeslaRobot width={30} height={30} />
                        </div>
                      )}
                      <div className="message-content">
                        <div className="message-text">{msg.text}</div>
                      </div>
                    </div>
                  ))}

                  {isTyping && (
                    <div className="chat-message ai">
                      <div className="message-avatar">
                        <TeslaRobot width={30} height={30} />
                      </div>
                      <div className="message-content">
                        <div className="typing-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <form className="chat-input-form" onSubmit={sendMessage}>
                  <input
                    type="text"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    placeholder="Type your message..."
                    disabled={isTyping || currentStep > 3}
                    className="chat-input"
                  />
                  <button
                    type="submit"
                    className="chat-send-button"
                    disabled={isTyping || !userInput.trim() || currentStep > 3}
                  >
                    Send
                  </button>
                </form>
              </div>

              {/* Task Assignment Section */}
              {currentStep >= 3 && (
                <div className="task-section">
                  <h3>Assign a Task</h3>
                  <p className="task-description">
                    Provide specific instructions for your AI employee based on their skills.
                  </p>

                  {!taskAssigned ? (
                    <form className="task-form" onSubmit={assignTask}>
                      <textarea
                        value={taskInput}
                        onChange={(e) => setTaskInput(e.target.value)}
                        placeholder={`Describe a task related to ${selectedEmployee.skills.slice(0, 2).join(' or ')}...`}
                        className="task-input"
                        rows={4}
                        disabled={isTyping}
                      ></textarea>
                      <div className="task-suggestions">
                        <p>Suggested tasks:</p>
                        <div className="suggestion-buttons">
                          {selectedEmployee.skills.includes('JavaScript') && (
                            <button
                              type="button"
                              onClick={() => setTaskInput("Create a JavaScript function to process and filter data based on business rules.")}
                              className="suggestion-button"
                            >
                              JavaScript Data Processing
                            </button>
                          )}
                          {selectedEmployee.skills.includes('Data Analysis') && (
                            <button
                              type="button"
                              onClick={() => setTaskInput("Analyze our customer data and provide insights on trends and opportunities.")}
                              className="suggestion-button"
                            >
                              Customer Data Analysis
                            </button>
                          )}
                          {selectedEmployee.skills.includes('Content Creation') && (
                            <button
                              type="button"
                              onClick={() => setTaskInput("Create a social media content calendar for the next two weeks.")}
                              className="suggestion-button"
                            >
                              Social Media Calendar
                            </button>
                          )}
                          {selectedEmployee.skills.includes('Project Management') && (
                            <button
                              type="button"
                              onClick={() => setTaskInput("Develop a project implementation plan with timeline and resource requirements.")}
                              className="suggestion-button"
                            >
                              Project Plan
                            </button>
                          )}
                        </div>
                      </div>
                      <button
                        type="submit"
                        className="assign-task-button"
                        disabled={isTyping || !taskInput.trim()}
                      >
                        Assign Task
                      </button>
                    </form>
                  ) : (
                    <div className="task-status">
                      <div className="assigned-task">
                        <h4>Assigned Task:</h4>
                        <p>{taskInput}</p>
                      </div>

                      {taskCompleted && (
                        <div className="task-result">
                          <h4>Task Result:</h4>
                          <pre className="result-content">{taskResult}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Final Step - Purchase Options */}
              {currentStep === 4 && taskCompleted && (
                <div className="purchase-section">
                  <h3>Ready to Get Started?</h3>
                  <p>
                    You've experienced how {selectedEmployee.name} can help with tasks related to {selectedEmployee.skills.join(', ')}.
                  </p>

                  <div className="purchase-options">
                    <div className="purchase-card">
                      <h4>Basic Plan</h4>
                      <div className="purchase-price">$20/month per seat</div>
                      <ul className="purchase-features">
                        <li>Pay only for time used</li>
                        <li>No long-term commitment</li>
                        <li>Available {selectedEmployee.availability}</li>
                        <li>All skills included</li>
                      </ul>
                      <button
                        className="purchase-button"
                        onClick={proceedToPurchase}
                      >
                        Add to Cart
                      </button>
                    </div>

                    <div className="purchase-card featured">
                      <div className="featured-badge">Best Value</div>
                      <h4>Enterprise Plan</h4>
                      <div className="purchase-price">
                        $15/month per seat
                        <span className="price-note">Save 25%</span>
                      </div>
                      <ul className="purchase-features">
                        <li>All basic plan benefits</li>
                        <li>Priority support</li>
                        <li>Annual billing option</li>
                        <li>Detailed usage reports</li>
                      </ul>
                      <button
                        className="purchase-button featured"
                        onClick={proceedToPurchase}
                      >
                        Add to Cart
                      </button>
                    </div>
                  </div>

                  <div className="demo-actions">
                    <button
                      className="new-demo-button"
                      onClick={startNewDemo}
                    >
                      Try Another Demo
                    </button>
                    <Link to="/employees" className="browse-button">
                      Browse All AI Employees
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="demo-features">
        <h2>Key Features of Our AI Employees</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">🧠</div>
            <h3>Specialized Skills</h3>
            <p>Each AI employee has specific skills and expertise, just like human professionals.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">⚡</div>
            <h3>Instant Availability</h3>
            <p>No hiring process or onboarding delays. Start working with your AI employee immediately.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">💰</div>
            <h3>Cost Effective</h3>
            <p>Pay only for the time you use, with no benefits, vacation, or overhead costs.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🔄</div>
            <h3>Seamless Integration</h3>
            <p>AI employees integrate with your existing tools and workflows through our API.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoPage;
